import React from 'react';
import { Card, Button } from 'antd-v5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

/**
 * EventCard component for displaying event summary
 * Used in Events list page
 */
const EventCard = ({ event, isPast, onClick, onLinksClick }) => {
  const {
    // eslint-disable-next-line no-unused-vars
    id,
    name,
    startDate,
    endDate,
    start_date,
    end_date,
    audienceCount,
    audience_count,
    appointmentCount,
    appointment_count,
    appointmentsCount,
    appointments_count,
    salesAmount,
    sales_amount,
  } = event;

  // Handle both camelCase and snake_case from API
  const eventStartDate = startDate || start_date;
  const eventEndDate = endDate || end_date;
  const audience = audienceCount || audience_count || 0;
  const appointments = appointmentCount || appointment_count || appointmentsCount || appointments_count || 0;
  const sales = salesAmount || sales_amount || 0;

  // Format date range
  const formatDateRange = () => {
    if (!eventStartDate) return 'No dates set';

    const start = dayjs(eventStartDate);
    const end = eventEndDate ? dayjs(eventEndDate) : null;

    if (!end || start.isSame(end, 'day')) {
      return start.format('ddd MMM D, YYYY');
    }

    // Check if same month and year
    if (start.isSame(end, 'month')) {
      return `${start.format('ddd MMM D')} - ${end.format('ddd MMM D, YYYY')}`;
    }

    return `${start.format('ddd MMM D, YYYY')} - ${end.format('ddd MMM D, YYYY')}`;
  };

  // Format currency
  const formatCurrency = amount => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const handleLinksClick = e => {
    e.stopPropagation();
    if (onLinksClick) {
      onLinksClick(event);
    }
  };

  return (
    <Card className="event-card" hoverable onClick={onClick}>
      <div className="event-card-content">
        {/* Header */}
        <div className="event-card-header">
          <h4 className="event-card-title">{name || 'Untitled Event'}</h4>
          <p className="event-card-date">{formatDateRange()}</p>
        </div>

        {/* Stats */}
        <div className="event-card-stats">
          <div className="event-card-stat">
            <span className="event-card-stat-label">Audience</span>
            <span className="event-card-stat-value">{audience.toLocaleString()}</span>
          </div>
          <div className="event-card-divider" />
          <div className="event-card-stat">
            <span className="event-card-stat-label">Appointments</span>
            <span className="event-card-stat-value">{appointments.toLocaleString()}</span>
          </div>
          {isPast && (
            <>
              <div className="event-card-divider" />
              <div className="event-card-stat">
                <span className="event-card-stat-label">Sales</span>
                <span className="event-card-stat-value">{formatCurrency(sales)}</span>
              </div>
            </>
          )}
        </div>

        {/* Event Links Button */}
        {onLinksClick && (
          <div className="event-card-links-container">
            <Button
              type="text"
              size="small"
              icon={<FontAwesomeIcon icon={['fal', 'link']} />}
              onClick={handleLinksClick}
              className="event-card-links-btn"
            >
              Event Links
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};

EventCard.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    audienceCount: PropTypes.number,
    audience_count: PropTypes.number,
    appointmentCount: PropTypes.number,
    appointment_count: PropTypes.number,
    appointmentsCount: PropTypes.number,
    appointments_count: PropTypes.number,
    salesAmount: PropTypes.number,
    sales_amount: PropTypes.number,
  }).isRequired,
  isPast: PropTypes.bool,
  onClick: PropTypes.func,
  onLinksClick: PropTypes.func,
};

EventCard.defaultProps = {
  isPast: false,
  onClick: () => {},
  onLinksClick: null,
};

export default EventCard;
